import React, { useState, useEffect, useMemo, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  DatePicker,
  Select,
  Row,
  Col,
  Typography,
  Space,
  Layout,
  theme,
  ConfigProvider,
  Spin,
  message,
} from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { fetchSessionAnalytics, clearSessions } from 'state/actions/session';
import { setBreadcrumb } from 'state/actions/breadcrumb';
import './index.css';

const DashboardBody = React.lazy(() => import('./DashboardBody'));

const { Title, Text } = Typography;
const { Content } = Layout;
const { RangePicker } = DatePicker;

dayjs.extend(isBetween);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);

function AllSessionDashboard() {
  const dispatch = useDispatch();

  const {
    analytics: analyticsData,
    loading,
    error,
    message: responseMessage,
  } = useSelector((state) => state.sessionAnalytic || {
    analytics: null,
    loading: false,
    error: null,
    message: '',
  });

  const [dateRange, setDateRange] = useState([]);
  const [selectedSite, setSelectedSite] = useState('all');
  const [allSites, setAllSites] = useState([]);

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  useEffect(() => {
    dispatch(fetchSessionAnalytics());
    dispatch(
      setBreadcrumb([
        { menu: 'AI Assistance Summary', link: '/session-analytics' },
      ])
    );

    return () => {
      dispatch(clearSessions());
    };
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error('Failed to load analytics data');
    }
  }, [error]);

  useEffect(() => {
    if (responseMessage && analyticsData) {
      message.success(responseMessage);
    }
  }, [responseMessage, analyticsData]);

  useEffect(() => {
    if (analyticsData?.sitePerformance && allSites.length === 0) {
      const sitesList = analyticsData.sitePerformance.map((site) => ({
        id: site.siteId,
        name: site.siteName,
      }));
      setAllSites(sitesList);
    }
  }, [analyticsData, allSites.length]);

  const filteredMetrics = useMemo(() => {
    if (!analyticsData) return {};

    if (selectedSite === 'all') {
      return analyticsData;
    }

    const filteredSitePerformance =
      analyticsData.sitePerformance?.filter(
        (site) => site.siteId === selectedSite
      ) || [];

    const filteredSiteGrowth =
      analyticsData.siteGrowth?.filter(
        (site) => site.siteId === selectedSite
      ) || [];

    const totalSessionsForFilteredSite = filteredSitePerformance.reduce(
      (sum, site) => sum + (site.sessionCount || 0),
      0
    );

    return {
      ...analyticsData,
      sitePerformance: filteredSitePerformance,
      siteGrowth: filteredSiteGrowth,
      totalSessions: totalSessionsForFilteredSite,
    };
  }, [analyticsData, selectedSite]);

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    
    const params = {};
    if (dates && dates.length === 2) {
      params.startTime = dates[0].startOf('day').toISOString();
      params.endTime = dates[1].endOf('day').toISOString();
    }
    
    if (selectedSite !== 'all') {
      params.siteId = selectedSite;
    }
    
    dispatch(fetchSessionAnalytics(params));
  };

  const handleSiteChange = (siteId) => {
    setSelectedSite(siteId);

    const params = {};
    if (siteId !== 'all') {
      params.siteId = siteId;
    }

    if (dateRange && dateRange.length === 2) {
      params.startTime = dateRange[0].startOf('day').toISOString();
      params.endTime = dateRange[1].endOf('day').toISOString();
    }
    
    dispatch(fetchSessionAnalytics(params));
  };
  
  return (
    <ConfigProvider>
      <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
        <Content style={{ padding: '24px' }}>
          <Spin spinning={loading} size="large">
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
              <Card
                style={{
                  marginBottom: 24,
                  background: colorBgContainer,
                  borderRadius: borderRadiusLG,
                }}
              >
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                  <Col xs={24} lg={12}>
                    <Title level={2} style={{ margin: 0 }}>
                      <CalendarOutlined style={{ marginRight: 8 }} />
                      Analytics Dashboard
                    </Title>
                    <Text type="secondary">
                      Session insights and performance metrics
                    </Text>
                    {analyticsData && (
                      <div style={{ marginTop: 8 }}>
                        <Text type="secondary">
                          Total Sessions: <strong>{filteredMetrics.totalSessions?.toLocaleString() || 0}</strong>
                        </Text>
                      </div>
                    )}
                  </Col>
                  <Col xs={24} lg={12}>
                    <Space
                      direction="vertical"
                      style={{ width: '100%' }}
                      size="middle"
                    >
                      <RangePicker
                        value={dateRange}
                        onChange={handleDateRangeChange}
                        style={{ width: '100%' }}
                        placeholder={['Start Date', 'End Date']}
                        format="YYYY-MM-DD"
                      />
                      <Select
                        value={selectedSite}
                        onChange={handleSiteChange}
                        style={{ width: '100%' }}
                        placeholder="Select site"
                        loading={loading && allSites.length === 0}
                      >
                        <Select.Option value="all">All Sites</Select.Option>
                        {allSites.map((site) => (
                          <Select.Option key={site.id} value={site.id}>
                            {site.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Space>
                  </Col>
                </Row>
              </Card>

              <Suspense fallback={<Spin size="large" style={{ display: 'block', marginTop: '50px' }} />}>
                {error && !loading && (
                  <Card style={{ textAlign: 'center', padding: '50px' }}>
                    <Title level={4} type="danger">
                      Failed to Load Analytics
                    </Title>
                    <Text type="secondary">
                      {error.message || 'An error occurred while fetching analytics data'}
                    </Text>
                  </Card>
                )}

                {!loading && !error && !analyticsData && (
                  <Card style={{ textAlign: 'center', padding: '50px' }}>
                    <Title level={4} type="secondary">
                      No Data Available
                    </Title>
                    <Text type="secondary">
                      No analytics data found for the selected criteria
                    </Text>
                  </Card>
                )}

                {!loading && !error && analyticsData && (
                  <DashboardBody
                    metrics={filteredMetrics}
                    dateRange={dateRange}
                    selectedSite={selectedSite}
                  />
                )}
              </Suspense>
            </div>
          </Spin>
        </Content>
      </Layout>
    </ConfigProvider>
  );
}

export default AllSessionDashboard;